import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Box, Button, Divider, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import React, { useCallback, useState } from 'react'
import { Navigation, Pagination, Autoplay, EffectCoverflow, Keyboard } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import FullscreenMediaViewer from './FullscreenMediaViewer'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-coverflow'
import 'swiper/css/zoom'
import 'swiper/css/thumbs'

/**
 * MediaGallery - A reusable media gallery component supporting both images and videos
 * with Swiper carousel and full-screen viewing
 *
 * @param {Object} props
 * @param {Array<Object>} props.media - Array of media items:
 *   - For images: { type: 'image', url: string }
 *   - For videos: { type: 'video', url: string, coverUrl: string }
 * @param {Array<string>} props.images - Legacy: Array of image URLs (will be converted to media format)
 * @param {string} [props.title='Media Gallery'] - Gallery section title
 * @param {boolean} [props.collapsible=true] - Whether the gallery can be collapsed
 * @param {boolean} [props.defaultExpanded=true] - Whether the gallery is expanded by default
 * @param {boolean} [props.autoplay=true] - Whether to autoplay the carousel
 * @param {number} [props.autoplayDelay=3000] - Autoplay delay in milliseconds
 * @param {string} [props.effect='coverflow'] - Swiper effect ('coverflow', 'slide', 'fade', 'cube', 'flip')
 */
const MediaGallery = ({
  media: mediaProp,
  images: imagesProp,
  title = 'Media Gallery',
  collapsible = true,
  defaultExpanded = true,
  autoplay = true,
  autoplayDelay = 3000,
  effect = 'coverflow',
  sx = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)

  // Convert images prop to media format for backwards compatibility
  const media = React.useMemo(() => {
    if (mediaProp) {
      return mediaProp
    }
    if (imagesProp && imagesProp.length > 0) {
      return imagesProp.map((url) => ({ type: 'image', url }))
    }
    return []
  }, [mediaProp, imagesProp])

  const handleMediaClick = useCallback((index) => {
    setCurrentMediaIndex(index)
    setFullscreenOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setFullscreenOpen(false)
  }, [])

  if (!media || media.length === 0) {
    return null
  }

  console.log('rendering media gallery with media')

  return (
    <Box sx={{ ...sx }}>
      {collapsible ? (
        <>
          <Box sx={{ alignItems: 'center', mt: 1, mb: 1 }}>
            <Button size="Large" color="primary" aria-expanded={expanded} aria-label={expanded ? 'Collapse gallery' : 'Expand gallery'} onClick={() => setExpanded(!expanded)}>
              <Typography variant="h5">{title}</Typography>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      ) : (
        <>
          <Box sx={{ alignItems: 'center', mt: 1, mb: 1, textAlign: 'center' }}>
            <Typography variant="h5">{title}</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      <Collapse in={collapsible ? expanded : true}>
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Swiper
            modules={[Navigation, Pagination, Autoplay, EffectCoverflow, Keyboard]}
            effect={effect}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: true,
            }}
            pagination={{ clickable: true }}
            navigation={true}
            autoplay={
              autoplay
                ? {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                  }
                : false
            }
            loop={false}
            initialSlide={currentMediaIndex}
            style={{
              paddingTop: '20px',
              paddingBottom: '50px',
            }}
            className="media-gallery-swiper"
          >
            {media.map((item, index) => (
              <SwiperSlide
                key={index}
                style={{
                  width: '300px',
                  height: '400px',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }}
                  onClick={() => handleMediaClick(index)}
                >
                  <Box
                    component="img"
                    src={item.type === 'video' ? item.coverUrl : item.url}
                    alt={`Gallery ${item.type} ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 2,
                      boxShadow: 3,
                      cursor: 'pointer',
                    }}
                  />
                  {item.type === 'video' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        borderRadius: '50%',
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 40 }} />
                    </Box>
                  )}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Collapse>

      {/* Fullscreen Media Viewer */}
      <FullscreenMediaViewer open={fullscreenOpen} onClose={handleClose} media={media} initialIndex={currentMediaIndex} />

      {/* Minimal custom styles for gallery Swiper */}
      <style>
        {`
          /* Disable hover effects on Swiper box */
          .media-gallery-swiper * {
            pointer-events: auto;
          }

          .media-gallery-swiper .swiper-button-next:hover,
          .media-gallery-swiper .swiper-button-prev:hover {
            opacity: 1;
          }

          .media-gallery-swiper .swiper-pagination-bullet:hover {
            opacity: 1;
          }
        `}
      </style>
    </Box>
  )
}

export default MediaGallery
